/* Medición Canalla · GA4 solo para interacciones propias.
   Páginas vistas, ecommerce y compras las envían los canales nativos de Shopify
   (Google & YouTube, Facebook & Instagram). Este archivo:
   - no envía nada sin consentimiento de analítica (Shopify Customer Privacy);
   - guarda los eventos hasta que el visitante decide en el banner y los descarta si rechaza;
   - carga la etiqueta de Google solo tras el consentimiento, sin página vista (send_page_view: false). */
(() => {
  'use strict';
  if (window.CanallaMeasurement) return;
  let config;
  try { config = JSON.parse(document.getElementById('CanallaMeasurementConfig').textContent); }
  catch { return; }
  if (!/^G-[A-Z0-9]+$/.test(config.measurementId || '')) return;

  // Clave interna → nombre del evento en GA4.
  const names = {
    home_click: 'canalla_home_click', collection_click: 'canalla_collection_click',
    gift_open: 'canalla_gift_open', gift_select: 'canalla_gift_select', gift_change: 'canalla_gift_change',
    gift_remove: 'canalla_gift_remove', gift_view_all: 'canalla_gift_view_all',
    form_attempt: 'canalla_form_attempt', faq_open: 'canalla_faq_open',
    video_start: 'canalla_video_start', video_complete: 'canalla_video_complete'
  };
  const fields = new Set(['action', 'placement', 'item_id', 'variant_id', 'mode', 'form_type']);
  const seen = new Map();
  const queue = [];
  const history = [];
  const query = new URLSearchParams(window.location.search);
  const debug = config.preview && ['1', 'send'].includes(query.get('canalla_debug'));
  const debugSend = debug && query.get('canalla_debug') === 'send';
  let tagLoaded = false, panel, log, summary;

  const privacy = () => window.Shopify?.customerPrivacy;
  // 'granted' | 'denied' | 'pending' (banner sin decidir) | 'unknown' (API aún no cargada)
  function consent() {
    const p = privacy();
    if (!p || typeof p.analyticsProcessingAllowed !== 'function') return 'unknown';
    try {
      if (p.analyticsProcessingAllowed() === true) return 'granted';
      if (typeof p.shouldShowBanner === 'function' && p.shouldShowBanner() === true) return 'pending';
      return 'denied';
    } catch { return 'unknown'; }
  }
  function marketingAllowed() {
    try { return privacy()?.marketingAllowed() === true; } catch { return false; }
  }
  function loadTag() {
    if (tagLoaded) return;
    tagLoaded = true;
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') window.gtag = function () { window.dataLayer.push(arguments); };
    const ads = marketingAllowed() ? 'granted' : 'denied';
    window.gtag('consent', 'default', {
      ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'denied'
    });
    window.gtag('consent', 'update', {
      analytics_storage: 'granted', ad_storage: ads, ad_user_data: ads, ad_personalization: ads
    });
    window.gtag('js', new Date());
    window.gtag('config', config.measurementId, { send_page_view: false });
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.measurementId);
    document.head.append(script);
  }
  function status() {
    return {
      preview: config.preview === true,
      consent: consent(),
      tagLoaded,
      queued: queue.length,
      destination: config.measurementId,
      sendEnabled: !config.preview || debugSend
    };
  }
  function render() {
    if (!summary) return;
    const s = status();
    summary.textContent = `Consentimiento: ${s.consent} · Etiqueta Google: ${s.tagLoaded ? 'cargada' : 'no cargada'} · ` +
      `En espera: ${s.queued} · Envío: ${s.sendEnabled ? 'habilitado' : 'desactivado en borrador'} · ${s.destination}`;
  }
  function audit(event, result) {
    // Solo nombres fijos de evento y estados. Nunca correos, códigos ni tokens.
    history.push({ event, result });
    if (history.length > 40) history.shift();
    if (debug && log) log.textContent = history.map(x => `${x.event}: ${x.result}`).join('\n');
    render();
  }
  function dispatch(name, data) {
    loadTag();
    try {
      window.gtag('event', name, { ...data, send_to: config.measurementId });
      audit(name, 'enviado a GA4');
      return true;
    } catch { audit(name, 'error de etiqueta'); return false; }
  }
  function track(event, input = {}) {
    const name = names[event];
    if (!name) return false;
    const data = {
      theme_environment: config.preview ? 'draft' : 'live',
      page_type: String(config.pageType || '').slice(0, 40),
      content_language: String(config.language || '').slice(0, 12),
      market_country: String(config.country || '').slice(0, 3)
    };
    for (const [key, value] of Object.entries(input)) {
      if (!fields.has(key)) continue;
      const safe = String(value ?? '');
      if (safe && /^[A-Za-z0-9_:\-]{1,80}$/.test(safe)) data[key] = safe;
    }
    if (config.preview) data.debug_mode = true;
    const fingerprint = name + JSON.stringify(data);
    const now = Date.now();
    for (const [key, time] of seen) if (now - time > 1000) seen.delete(key);
    if (seen.has(fingerprint)) return false;
    seen.set(fingerprint, now);
    if (config.preview && !debugSend) { audit(name, 'solo prueba local'); return false; }
    const state = consent();
    if (state === 'granted') return dispatch(name, data);
    if (state === 'pending' || state === 'unknown') {
      if (queue.length < 20) queue.push([name, data]);
      audit(name, 'en espera del banner de cookies');
      return false;
    }
    audit(name, 'bloqueado: analítica rechazada');
    return false;
  }
  function onConsentChange() {
    const state = consent();
    if (state === 'granted') {
      while (queue.length) { const [name, data] = queue.shift(); dispatch(name, data); }
    } else if (state === 'denied') {
      queue.length = 0;
    }
    render();
  }
  window.CanallaMeasurement = Object.freeze({ track, status, history: () => history.slice() });

  document.addEventListener('visitorConsentCollected', onConsentChange);
  if (!privacy() && typeof window.Shopify?.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], onConsentChange);
  }

  document.addEventListener('canalla:home-action', e => track('home_click', {
    action: e.detail?.action, item_id: e.detail?.productId, placement: 'home'
  }));
  document.addEventListener('canalla:collection-action', e => track('collection_click', {
    action: e.detail?.action, item_id: e.detail?.productId, placement: 'collection'
  }));
  document.addEventListener('click', e => {
    const target = e.target?.closest?.('button,a');
    if (!target || target.disabled) return;
    const gift = target.closest('canalla-gift');
    if (gift) {
      const action = target.dataset.action;
      const mapping = { choose: 'gift_select', change: 'gift_change', remove: 'gift_remove', all: 'gift_view_all' };
      const event = action === 'toggle' && target.getAttribute('aria-expanded') === 'false'
        ? 'gift_open' : mapping[action];
      if (event) track(event, { placement: gift.dataset.placement || 'cart',
        variant_id: target.dataset.variant,
        mode: gift.querySelector('.cg-preview') ? 'preview' : 'live' });
      return;
    }
  }, true);
  document.addEventListener('submit', e => {
    const form = e.target;
    if (!form?.matches?.('form')) return;
    const id = form.id || '';
    const tags = form.querySelector('input[name="contact[tags]"]')?.value || '';
    const formType = /newsletter/i.test(id) || tags.split(',').includes('newsletter')
      ? 'newsletter' : /contact/i.test(id) ? 'contact' : '';
    if (formType) track('form_attempt', { form_type: formType });
  }, true);
  document.addEventListener('toggle', e => {
    if (e.target?.open && e.target.matches?.('.cc-editorial__question,.ch-faq details,.canalla-pdp__faq details')) {
      track('faq_open', { action: 'open' });
    }
  }, true);
  const videos = new WeakSet();
  document.addEventListener('play', e => {
    if (e.target?.tagName !== 'VIDEO' || videos.has(e.target)) return;
    videos.add(e.target);
    track('video_start', { placement: config.pageType === 'product' ? 'pdp' : 'home' });
  }, true);
  document.addEventListener('ended', e => {
    if (e.target?.tagName === 'VIDEO') track('video_complete', {
      placement: config.pageType === 'product' ? 'pdp' : 'home'
    });
  }, true);

  if (debug) {
    panel = document.createElement('details');
    panel.id = 'CanallaMeasurementQA';
    panel.open = true;
    panel.style.cssText = 'position:fixed;bottom:64px;left:8px;z-index:999999;max-width:calc(100vw - 16px);width:440px;background:#fff;color:#111;border:2px solid #222;padding:8px;font:12px/1.5 monospace;box-sizing:border-box';
    const label = document.createElement('summary');
    label.textContent = 'Comprobación de medición · solo borrador';
    summary = document.createElement('p');
    log = document.createElement('pre');
    log.style.cssText = 'white-space:pre-wrap;max-height:180px;overflow:auto';
    panel.append(label, summary, log);
    document.body.append(panel);
    render();
  }
})();